import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { HeartIcon as Heart, CalendarIcon as Calendar, ArrowRightIcon as ArrowRight, CrownIcon as Crown, CheckCircleIcon as CheckCircle, StarIcon as Star, ArrowsClockwiseIcon as ArrowsClockwise } from "@phosphor-icons/react";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";

/**
 * "List this dog for breeding" panel — Dog detail Overview, owner-only.
 *
 * Three states it renders:
 *
 *  1. Already has an active breeding listing → "Live in the breeding
 *     directory" card with expiry + featured/auto-renew badges +
 *     link to listing edit page.
 *
 *  2. No listing + user has Pro/Elite/Basic → tier-aware free-checkout
 *     UI ("free with your plan", one-click create). For Basic we also
 *     show their monthly quota usage so they know when paid kicks in.
 *
 *  3. No listing + no plan → standard pay-per-listing pitch with the
 *     three duration tiers.
 *
 * This closes the founder-flagged gap that the Breeding Directory was
 * disconnected from the Dogs flow — now every dog's page has a direct
 * "List for breeding" entry point with the correct billing path
 * pre-resolved.
 */
export default function BreedingDirectoryPanel({ dog, isOwner }) {
  const [listing, setListing] = useState(null);
  const [tier, setTier] = useState(null);
  const [quota, setQuota] = useState(null); // {used, total} for Basic only
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const dogId = dog?.id;

  useEffect(() => {
    if (!dogId || !isOwner) return;
    Promise.all([
      api.get(`/breeding/for-dog/${dogId}`, { silent: true }).catch(() => ({ data: null })),
      api.get("/billing/my-subscription", { silent: true }).catch(() => ({ data: null })),
    ]).then(([l, sub]) => {
      setListing(l.data);
      setTier(sub.data?.subscription?.plan || null);
    });
  }, [dogId, isOwner]);

  if (!isOwner || !dogId) return null;

  // Owner already has a live listing — show its status.
  if (listing && listing.expires_at && new Date(listing.expires_at) > new Date()) {
    return (
      <div className="bg-white border border-[#EAEAEA] rounded-xl p-5" data-testid="breeding-panel-active">
        <div className="flex items-start gap-4">
          <Heart size={28} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="font-spectral text-lg">Live in the breeding directory</h3>
              {listing.featured && (
                <span data-testid="breeding-panel-featured" className="text-[10px] font-bold uppercase tracking-wider bg-gold-gradient text-ink rounded-full px-2 py-0.5">
                  ★ Featured
                </span>
              )}
              {listing.auto_renew && (
                <span data-testid="breeding-panel-autorenew" className="text-[10px] font-bold uppercase tracking-wider bg-[#EEF8F1] text-[#0F7B3E] border border-[#BFE8C9] rounded-full px-2 py-0.5">
                  ↻ Auto-renew on
                </span>
              )}
            </div>
            <p className="text-[13px] text-[#6B6B6B] mt-1">
              Active until <strong className="text-ink">{listing.expires_at?.slice(0, 10)}</strong>
              {typeof listing.renewal_count === "number" && listing.renewal_count > 0 && (
                <span> · renewed {listing.renewal_count}×</span>
              )}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/breeding/my"
                data-testid="breeding-panel-manage-link"
                className="inline-flex items-center gap-1 text-[13px] text-ink hover:text-crimson font-bold underline"
              >
                Manage listing <ArrowRight size={11} weight="bold" />
              </Link>
              <Link
                to="/breeding"
                className="inline-flex items-center gap-1 text-[12px] text-[#6B6B6B] hover:text-ink"
              >
                View directory
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Owner has no active listing — show tier-aware sign-up.
  const onListFree = async (days) => {
    setBusy(true);
    try {
      const res = await api.post("/billing/checkout", {
        package_id: `breeding_listing_${days === 30 ? "30d" : days === 90 ? "90d" : "1y"}`,
        dog_id: dogId,
        origin_url: webOrigin(),
      });
      if (res.data?.free) {
        toast.success(`Listed in the breeding directory${res.data.quota_used ? ` (free ${res.data.quota_used}/${res.data.quota_total} this month)` : ""}.`);
        // Refresh local state
        const l = await api.get(`/breeding/for-dog/${dogId}`, { silent: true }).catch(() => ({ data: null }));
        setListing(l.data);
      } else if (res.data?.url) {
        // Fell through to Stripe checkout
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isFreeForTier = tier === "pro" || tier === "kennel_elite";
  const isBasic = tier === "basic";

  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-5" data-testid="breeding-panel-pitch">
      <div className="flex items-start gap-4">
        <Heart size={28} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-spectral text-lg">List this dog for breeding</h3>
            {isFreeForTier && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FBF7EC] text-[#8A6A00] border border-[#E8D9A6] rounded-full px-2 py-0.5">
                ✓ {isNativeApp() ? "Included with your plan" : <>Free with {tier === "kennel_elite" ? "Kennel Elite" : "Breeder Pro"}</>}
              </span>
            )}
          </div>
          <p className="text-[13px] text-[#3F3F3F] mt-2 leading-relaxed">
            Add {dog.name} to the public Breeding Directory so other breeders
            looking for a stud or dam can find them. Includes filter visibility
            (sex, breed, color, age), Express Interest dialog, and heat-cycle log.
          </p>

          {/* Elite — featured + auto-renew callout */}
          {tier === "kennel_elite" && (
            <p className="mt-3 inline-flex items-start gap-2 text-[12px] bg-[#FBF7EC] text-[#8A6A00] border border-[#E8D9A6] rounded-lg px-3 py-2">
              <Crown size={14} weight="fill" className="mt-0.5" />
              <span><strong>{isNativeApp() ? "Included with your plan:" : "Kennel Elite perk:"}</strong> your listing rotates to the top of the directory (featured) AND auto-renews on expiry — no action required.</span>
            </p>
          )}

          {/* Pro — free unlimited */}
          {tier === "pro" && (
            <p className="mt-3 inline-flex items-start gap-2 text-[12px] bg-[#F1F3F8] text-[#3A4670] border border-[#CFD6E8] rounded-lg px-3 py-2">
              <CheckCircle size={14} weight="fill" className="mt-0.5" />
              <span><strong>{isNativeApp() ? "Included with your plan:" : "Breeder Pro perk:"}</strong> unlimited free breeding-directory listings at every duration tier.</span>
            </p>
          )}

          {/* Basic — quota visible */}
          {isBasic && !isIOS() && (
            <p className="mt-3 inline-flex items-start gap-2 text-[12px] bg-[#EEF8F1] text-[#0F7B3E] border border-[#BFE8C9] rounded-lg px-3 py-2">
              <Star size={14} weight="fill" className="mt-0.5" />
              <span><strong>Breeder Basic perk:</strong> first <strong>2 free 30-day listings per month</strong>. Beyond that or for 90/365-day tiers, regular pricing applies ($14.99 / $29.99 / $49.99).</span>
            </p>
          )}

          {/* Action buttons — tier-aware */}
          <div className="mt-4 flex flex-wrap gap-2">
            {isIOS() && !isFreeForTier ? (
              <IosPurchaseNotice label="Breeding directory listing" className="w-full" />
            ) : isFreeForTier ? (
              <>
                <Button
                  onClick={() => onListFree(30)}
                  disabled={busy}
                  className="bg-ink hover:bg-crimson text-white rounded-full"
                  data-testid="breeding-panel-list-30"
                >
                  <Calendar size={14} weight="bold" /> List for 30 days — free
                </Button>
                <Button
                  onClick={() => onListFree(90)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full"
                  data-testid="breeding-panel-list-90"
                >
                  90 days — free
                </Button>
                <Button
                  onClick={() => onListFree(365)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full"
                  data-testid="breeding-panel-list-365"
                >
                  365 days — free
                </Button>
              </>
            ) : isBasic ? (
              <>
                <Button
                  onClick={() => onListFree(30)}
                  disabled={busy}
                  className="bg-ink hover:bg-crimson text-white rounded-full"
                  data-testid="breeding-panel-list-30"
                >
                  <Calendar size={14} weight="bold" /> List for 30 days
                </Button>
                <Button
                  onClick={() => onListFree(90)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full"
                  data-testid="breeding-panel-list-90"
                >
                  90 days · $29.99
                </Button>
                <Button
                  onClick={() => onListFree(365)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full"
                  data-testid="breeding-panel-list-365"
                >
                  365 days · $49.99
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => onListFree(30)}
                  disabled={busy}
                  className="bg-ink hover:bg-crimson text-white rounded-full"
                  data-testid="breeding-panel-list-30"
                >
                  <Calendar size={14} weight="bold" /> 30 days · $14.99
                </Button>
                <Button
                  onClick={() => onListFree(90)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full"
                  data-testid="breeding-panel-list-90"
                >
                  90 days · $29.99
                </Button>
                <Button
                  onClick={() => onListFree(365)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full"
                  data-testid="breeding-panel-list-365"
                >
                  365 days · $49.99
                </Button>
                <Button
                  onClick={() => navigate("/billing")}
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-[12px]"
                  data-testid="breeding-panel-upgrade-link"
                >
                  Or upgrade to Pro for unlimited free <ArrowRight size={11} weight="bold" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
